import util from 'util';
import { CONSTANT } from '@const';
import {
	DeviceReading,
	DeviceReadingPayload,
	Page,
	PageFilter,
	ReadingRequestItem,
	ReadingStatus,
} from '@types';
import { compareDesc } from 'date-fns';

import { ApiParams, ApiService } from '../api/ApiService';

export class DeviceReadingService extends ApiService {
	static async getDeviceReadings(
		clientId: string,
		deviceId: string,
		pageFilter?: PageFilter,
	): Promise<Page<DeviceReading>> {
		const params: ApiParams = {
			method: 'GET',
			url: util.format(`${CONSTANT.DEVICE_READINGS_API_URL}`, clientId, deviceId),
			queryParams: pageFilter,
		};
		return await this.executeApiRequest(params);
	}

	static async createDeviceReading(
		clientId: string,
		deviceId: string,
		readingCreation: DeviceReadingPayload,
	): Promise<DeviceReading> {
		const params: ApiParams = {
			method: 'POST',
			headers: { itpwafexclude: CONSTANT.ITPWAF_EXCLUDE },
			url: util.format(`${CONSTANT.DEVICE_READINGS_API_URL}`, clientId, deviceId),
			data: readingCreation,
		};
		return await this.executeApiRequest(params);
	}

	static async updateDeviceReading(
		clientId: string,
		deviceId: string,
		deviceReading: Partial<DeviceReading>,
	): Promise<DeviceReading> {
		const params: ApiParams = {
			method: 'PUT',
			url: util.format(
				`${CONSTANT.DEVICE_READING_PHOTO_API_URL}`,
				clientId,
				deviceId,
				deviceReading.id,
			),
			data: deviceReading,
		};
		return await this.executeApiRequest(params);
	}

	static async getDeviceReadingImage(
		clientId: string,
		deviceId: string,
		readingId: string,
	): Promise<string> {
		const params: ApiParams = {
			method: 'GET',
			url: util.format(`${CONSTANT.DEVICE_READING_PHOTO_API_URL}`, clientId, deviceId, readingId),
		};
		return await this.executeApiRequest(params);
	}

	static async getLastAcceptableReading(
		clientId: string,
		deviceId: string,
		pageFilter?: PageFilter,
	): Promise<DeviceReading | undefined> {
		return await this.getDeviceReadings(clientId, deviceId, pageFilter).then(data =>
			data.items
				.filter(
					reading =>
						reading.status == ReadingStatus.VERIFIED ||
						reading.status == ReadingStatus.REQUIRES_REVIEW ||
						reading.status == ReadingStatus.SUBMITTED,
				)
				.sort((a, b) => {
					return compareDesc(new Date(a.readingDate), new Date(b.readingDate));
				})
				.shift(),
		);
	}

	static async getReadingRequests({
		status,
		limit,
		offset,
	}: {
		status?: string;
		limit?: number;
		offset?: number;
	}): Promise<Page<ReadingRequestItem>> {
		const params: ApiParams = {
			method: 'GET',
			url: `${CONSTANT.READING_REQUESTS_API_URL}`,
			queryParams: {
				status,
				offset,
				limit,
			},
		};
		return await this.executeApiRequest(params);
	}
}
